<script>
import { Bus } from '@common/bus';
import Notification from '@common/components/FdxNotification.vue';
import Alert from '@common/components/LegacyAlert.vue';
import AppHeader from '@mms/components/AppHeader.vue';
import { useAppSettings } from '@mms/composables/useAppSettings';
import { useUserStore } from '@mms/composables/useUserStore';
import menu from '@mms/menu';
import * as Sentry from '@sentry/vue';
import mixpanel from 'mixpanel-browser';
import moment from 'moment';

import Sidebar from './SideBar.vue';

export default {
  components: {
    Alert,
    AppHeader,
    Notification,
    Sidebar,
  },
  computed: {
    isLoaded() {
      return !this.isLoading;
    },
  },
  created() {
    this.configureLocaleWeekStart();
  },
  data() {
    return {
      alert: {
        autoClose: 0,
        confirmationCancel: null,
        confirmationProceed: null,
        message: '',
        subMessage: '',
        type: null,
      },
      menu,
      mobileMenuOpen: false,
      notification: {
        autoClose: 0,
        message: '',
        type: null,
      },
    };
  },
  methods: {
    closeNotification() {
      this.notification.type = null;
      this.notification.autoClose = 0;
      this.notification.message = '';
    },
    configureLocaleWeekStart() {
      moment.updateLocale('en', {
        week: {
          dow: 1, // Monday is the first day of the week.
          doy: 1, // The week that contains Jan 1st is the first week of the year.
        },
      });
    },
    logout() {
      mixpanel.reset();
      Sentry.setUser(null);
      localStorage.clear();
      location.replace('/logout');
    },
  },
  async mounted() {
    Bus.$on('alert', (data) => {
      this.alert = data;
    });
    Bus.$on('notification', (data) => {
      this.notification = data;
    });
  },
  setup() {
    const { isAuthenticated, isLoading, setUserInfo, user } = useUserStore();
    const { isHeaderDisplayed, isSideBarDisplayed } = useAppSettings();

    return {
      isAuthenticated,
      isHeaderDisplayed,
      isLoading,
      isSideBarDisplayed,
      setUserInfo,
      user,
    };
  },
  watch: {
    '$route.path'() {
      this.mobileMenuOpen = false;
    },
    isHeaderDisplayed: {
      handler(value) {
        // as per design the recaptcha badge is only showing in the login,otp,mobile-register screens
        if (value) {
          document.body.classList.remove('recaptcha');
        } else {
          document.body.classList.add('recaptcha');
        }
      },
      immediate: true,
    },
  },
};
</script>
<template>
  <div>
    <div v-if="!isLoaded">
      <screen-loader />
    </div>
    <div v-show="isLoaded" class="wrapper ltr" dir="ltr">
      <alert
        v-if="alert.type"
        :message="alert.message"
        :sub-message="alert.subMessage"
        :type="alert.type"
        :auto-close="alert.autoClose"
        :confirmation-proceed="alert.confirmationProceed"
        :confirmation-cancel="alert.confirmationCancel"
        :confirmation-proceed-btn-text="alert.confirmationProceedBtnText"
        :confirmation-cancel-btn-text="alert.confirmationCancelBtnText"
        :is-danger="alert.isDanger"
        :title="alert.title"
      />

      <notification
        v-if="notification.type"
        :message="notification.message"
        :type="notification.type"
        :auto-close="notification.autoClose"
        :class="{ '!w-full ps-24': !isSideBarDisplayed }"
        @expired="closeNotification"
      />

      <app-header v-if="isHeaderDisplayed">
        <div class="flex">
          <div
            class="mobile-menu ms-2 inline-block rounded-md border pt-4 lg:hidden"
            :class="{ open: mobileMenuOpen }"
            @click.stop="
              () => {
                mobileMenuOpen = !mobileMenuOpen;
              }
            "
          >
            <div class="bar1"></div>
            <div class="bar2"></div>
            <div class="bar3"></div>
          </div>
        </div>
      </app-header>
      <template v-if="isSideBarDisplayed">
        <div class="lg:hidden">
          <transition name="show">
            <sidebar
              v-if="mobileMenuOpen"
              v-click-outside="
                () => {
                  mobileMenuOpen = false;
                }
              "
              :menu
              class="block w-1/2 lg:w-full"
              @close="mobileMenuOpen = false"
            />
          </transition>
        </div>

        <div class="hidden lg:block">
          <sidebar :menu></sidebar>
        </div>
      </template>

      <main :id="isSideBarDisplayed ? 'main' : 'main-authorized'" data-testid="main">
        <router-view v-slot="{ Component, route }">
          <transition :name="route.meta.transition || 'fade'" mode="out-in" appear>
            <component
              :is="Component"
              :key="$route.path"
              :class="{ 'pb-10': isSideBarDisplayed }"
            />
          </transition>
        </router-view>
      </main>
    </div>
  </div>
</template>

<style>
.grecaptcha-badge {
  visibility: hidden;
}

body.recaptcha .grecaptcha-badge {
  visibility: visible;
}
</style>
